/**
 * AddAgreementPartyModal — Session A4 (2026-06-10, §3g Phase A4).
 *
 * Modal-that-stages for Person↔Agreement relationships, closing the last
 * confirmed C-107 Pattern A violator (#7: "+ Add party" was a navigate-away
 * Link to /agreement-parties/new). Mirrors AddAccountPartyModal (#411) and
 * AddPolicyPartyModal (#368) in shape and convention.
 *
 * Field scope (per PLAN_TO_ZERO Session A4): the COMMON case — Person,
 * Role (HOLDER / REPRESENTATIVE / BENEFICIARY), UBO toggle (beneficiaries
 * on Empresa agreements only), Notes. The specialist linkage fields
 * (related_party_id, representative power type, beneficiary subtype, POA
 * reference) stay on the row's detail page (/agreement-parties/:id) where
 * the full form lives — stage the row here, refine it there after Save if
 * needed. REPRESENTATIVE rows need a power type server-side, so for that
 * role the modal asks for it (LPOA / MANDATO) — the backend CHECK
 * constraint rejects a representative without one, and a guaranteed-409
 * staging row would be a trap.
 *
 * The modal returns the built payload + the resolved Person display name to
 * the parent via `onStage(payload, personDisplayName)`. The parent appends
 * a CREATE op to its pendingPartyOps queue; the batch commits through
 * maker-checker when the user clicks Save Changes.
 *
 * Per the app-wide convention (#101 won't-do): the picker is NOT filtered
 * against already-assigned Person IDs — duplicates are caught by the
 * backend's partial-unique 409 on Save and surfaced as a Failed marker on
 * the pending row.
 */
import {
  Alert, Button, Group, Modal, Select, Stack, Switch, Text, Textarea,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { PersonPicker } from './EntityPicker';
import {
  PartyAddChooser, PartyCreatePersonStep, newPartyPersonUrl, usePartyAddStep,
} from './PartyAddSteps';
import { usePerson } from '../hooks/usePersons';
import type {
  AgreementPartyCreateInput, PartyRole, RepresentativePower, UUID,
} from '../api/types';

const ROLE_OPTS: { value: PartyRole; label: string }[] = [
  { value: 'HOLDER', label: 'Holder' },
  { value: 'REPRESENTATIVE', label: 'Representative' },
  { value: 'BENEFICIARY', label: 'Beneficiary' },
];

const POWER_OPTS: { value: RepresentativePower; label: string }[] = [
  { value: 'LPOA', label: 'LPOA (natural-person holder)' },
  { value: 'MANDATO', label: 'Mandato (legal-entity holder)' },
];

interface FormValues {
  person_id: string;
  role: PartyRole;
  is_ubo: boolean;
  representative_power_type: string;
  notes: string;
}

const EMPTY: FormValues = {
  person_id: '',
  role: 'HOLDER',
  is_ubo: false,
  representative_power_type: '',
  notes: '',
};

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Agreement this party will be added to. Label displayed read-only at
   *  the top of the modal for confirmation. */
  agreementId: UUID;
  agreementLabel: string;
  /** The agreement's client type. UBO designation is a legal-entity
   *  concept — the backend 422s is_ubo=True on Particular agreements
   *  (ubo_requires_empresa guard), so the UBO switch only renders for
   *  Empresa, matching the old full-page form's behaviour. */
  clientType: 'Particular' | 'Empresa';
  /** Called with the built payload + the resolved Person display name (for
   *  friendly rendering on the pending row before it exists in the DB).
   *  The parent appends a CREATE op with a freshly-minted tempKey. */
  onStage: (payload: AgreementPartyCreateInput, personDisplayName: string | null) => void;
  /** #6 (2026-09-13): when set, the modal opens straight on the Attach step with
   *  this person pre-selected — the resume leg of the "Create new person"
   *  round-trip (person created on /persons/new, id ferried back here so defining
   *  its role is the immediate next action). */
  initialPersonId?: string | null;
}

export function AddAgreementPartyModal({
  opened, onClose, agreementId, agreementLabel, clientType, onStage, initialPersonId,
}: Props) {
  const form = useForm<FormValues>({
    initialValues: EMPTY,
    validate: {
      person_id: (v) => (v.length >= 32 ? null : 'Person required'),
      representative_power_type: (v, values) =>
        values.role === 'REPRESENTATIVE' && !v
          ? 'Required for representatives'
          : null,
    },
  });

  const navigate = useNavigate();
  // #6: chooser step ('choose' → 'create' | 'attach'); 'attach' wraps the stage
  // form. P18 (2026-10-10): the steps are shared with the Policy and Account
  // modals (PartyAddSteps). Clean slate on every open — dismiss = discard (§3g);
  // a resumed create round-trip opens on 'attach' with the person pre-selected.
  const [path, setPath] = usePartyAddStep(
    opened, initialPersonId, (pid) => form.setValues({ ...EMPTY, person_id: pid }),
  );

  // #6 (Pedro 2026-09-13): "Create new person" navigates to the new-Person form with
  // return_to = this agreement's Parties tab AND return_field=new_party_person. Person↔
  // Agreement is a role-bearing party that STAGES on the agreement's Edit→Save transaction
  // (§3g). On save, PersonDetailPage ferries the new id back onto the Parties-tab URL; the
  // agreement page re-enters Edit mode and reopens this modal on the Attach step with the new
  // person pre-selected (initialPersonId) — so the immediate next action is to pick its role,
  // Stage, and Save (which flips the person to Active via has_active_role). An RM-created
  // person is filed for approval instead and routes to its change-request page (no ferry).
  function handleCreateNew() {
    onClose();
    navigate(newPartyPersonUrl(`/agreements/${agreementId}?tab=parties`));
  }

  const personQ = usePerson(form.values.person_id || undefined);
  const personDisplayName = personQ.data?.display_name ?? null;

  const isBeneficiary = form.values.role === 'BENEFICIARY';
  const isRepresentative = form.values.role === 'REPRESENTATIVE';
  const uboAllowed = isBeneficiary && clientType === 'Empresa';

  function handleSave(values: FormValues) {
    const payload: AgreementPartyCreateInput = {
      agreement_id: agreementId,
      person_id: values.person_id,
      role: values.role,
      // C-115 (2026-06-10): always sent explicitly. The backend's RM
      // pending-path response stub required is_ubo; a sparse payload
      // 500'd. The server now also defaults it (belt and braces).
      is_ubo: values.role === 'BENEFICIARY' && values.is_ubo,
    };
    if (values.role === 'REPRESENTATIVE' && values.representative_power_type) {
      payload.representative_power_type =
        values.representative_power_type as RepresentativePower;
    }
    if (values.notes) payload.notes = values.notes;
    onStage(payload, personDisplayName);
    onClose();
  }

  // A4 smoke Test 1 fix (2026-06-10): the modal renders within the page's
  // <form id="agreement-form"> React subtree (Mantine portals don't stop
  // React event propagation), so without stopPropagation the inner submit
  // ALSO fires the page's Save — auto-committing the just-staged party and
  // exiting Edit mode. Same trap InteractionFormModal documented and fixed
  // for #157; generalised to all staging modals + recorded as C-113.
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    form.onSubmit(handleSave)(e);
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Add party to this agreement" size="lg">
      {path === 'choose' && (
        <PartyAddChooser recordLabel={agreementLabel} recordNoun="agreement"
          onPick={setPath} onCancel={onClose} />
      )}

      {path === 'create' && (
        <PartyCreatePersonStep onBack={() => setPath('choose')} onOpenForm={handleCreateNew} />
      )}

      {path === 'attach' && (
      <form onSubmit={handleFormSubmit}>
        <Stack>
          <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm" fw={500}>{agreementLabel}</Text>
            <Text size="xs" c="dimmed">
              The new party row is staged and only commits when you click
              Save Changes on the agreement page. Specialist linkage (which
              holder a representative acts for, beneficiary subtype, POA
              reference) can be refined on the party&apos;s own page after Save.
            </Text>
          </Alert>

          <PersonPicker
            label="Person"
            description="Pick the person (or legal entity) to attach in the selected role."
            value={form.values.person_id || null}
            onChange={(v) => form.setFieldValue('person_id', v ?? '')}
            error={form.errors.person_id as string | undefined}
          />

          <Select
            label="Role"
            data={ROLE_OPTS}
            value={form.values.role}
            onChange={(v) => {
              if (!v) return;
              form.setFieldValue('role', v as PartyRole);
              // Role-scoped fields reset when the role changes so a stale
              // UBO flag / power type can't ride along on the payload.
              form.setFieldValue('is_ubo', false);
              form.setFieldValue('representative_power_type', '');
            }}
            allowDeselect={false}
            required
          />

          {isRepresentative && (
            <Select
              label="Power type"
              description="LPOA for natural-person holders, Mandato for legal-entity holders."
              data={POWER_OPTS}
              value={form.values.representative_power_type || null}
              onChange={(v) => form.setFieldValue('representative_power_type', v ?? '')}
              error={form.errors.representative_power_type as string | undefined}
              required
            />
          )}

          {uboAllowed && (
            <Switch
              label="UBO (ultimate beneficial owner)"
              checked={form.values.is_ubo}
              onChange={(e) => form.setFieldValue('is_ubo', e.currentTarget.checked)}
            />
          )}

          <Textarea
            label="Notes"
            placeholder="Optional"
            value={form.values.notes}
            onChange={(e) => form.setFieldValue('notes', e.currentTarget.value)}
            autosize
            minRows={2}
          />

          <Group justify="space-between" gap="xs">
            <Button variant="default" onClick={() => setPath('choose')}>Back</Button>
            <Button type="submit">Stage party</Button>
          </Group>
        </Stack>
      </form>
      )}
    </Modal>
  );
}
